import { conv, or, integer, seqor, without, Or, rep, opt } from '../parser';
import {
  OffsetType,
  CharacterOffset,
  TemporalOffset,
  SpatialOffset,
  TemporalSpatialOffset,
  Offset,
  Child,
  LocalPath,
  LocalPaths,
  Path,
  CFIRange,
  TextLocationAssertion,
  Parameter,
  Assertion,
} from './types';

export const specialChars = conv(/[\^\[\]\(\),;=]/, (m:RegExpExecArray) => m[0]);

export const escapedSpecialChars = conv(['^', specialChars], ([, ch]:[any, string]) => ch);

export const characterEscapedSpecial: Or<string, string> = or(
  without(character, specialChars),
  escapedSpecialChars,
);

export const stringEscapedSpecialChars = conv(
  rep(characterEscapedSpecial, undefined, 1),
  (val => val.join('')),
);

export const value = stringEscapedSpecialChars;

export const valueNoSpace = conv(
  rep(without(characterEscapedSpecial, /\s/), undefined, 1),
  (val => val.join('')),
);

export const csv = rep(value, ',', 1);

export const parameter = conv(
  [';', valueNoSpace, '=', csv],
  ([, key, , values]:[any, string, any, string[]]): Parameter => {
    return { key, values };
  },
);

export const textLocationAssertion = conv(
  seqor(value, [',', value]),
  (value: [string] | [[',', string]] | [string, [',', string]]) => {
    if (value.length === 1) {
      const [v] = value;
      if (typeof v === 'string') {
        return new TextLocationAssertion(v);
      }
      return new TextLocationAssertion(undefined, v[1]);
    }
    if (value.length === 2) {
      const [before, [, after]] = value as [string, [',', string]];
      return new TextLocationAssertion(before, after);
    }
  },
);

export const assertion = conv(
  seqor(
    textLocationAssertion,
    rep(parameter),
  ),
  (v) => {
    if (v.length === 1) {
      const [v0] = v;
      if (v0 instanceof TextLocationAssertion) {
        return new Assertion(v0);
      }
      return new Assertion(undefined, v0);
    }
    const [v0, v1] = v as [TextLocationAssertion, Parameter[]];
    return new Assertion(v0, v1);
  },
);

export const characterOffset = conv([':', integer], ([, offset]:[any, number]): CharacterOffset => {
  return { offset, type: OffsetType.CHARACTER };
});

export const numberParser = conv(
  /(?:[1-9]\d*|0)(?:\.\d*[1-9])?/,
  (m: RegExpMatchArray) => parseInt(m[0], 10),
);

export const temporalOffset = conv(
  ['~', numberParser],
  ([, offset]:[any, number]): TemporalOffset => ({ offset, type: OffsetType.TEMPORAL }),
);

export const spatialOffset = conv(
  ['@', numberParser, ':', numberParser],
  ([, offsetX, , offsetY]:[any, number, any, number]): SpatialOffset => {
    return { offsetX, offsetY, type: OffsetType.SPATIAL };
  },
);

export const offset = conv(
    or(
      characterOffset,
      conv(seqor(temporalOffset, spatialOffset), (value) => {
        if (value.length === 1) {
          return value[0];
        }
        const [temporal, spatial] = value as [TemporalOffset, SpatialOffset];
        return {
          type: OffsetType.TEMPORAL_SPATIAL,
          offset: temporal.offset,
          offsetX: spatial.offsetX,
          offsetY: spatial.offsetY,
        } as TemporalSpatialOffset;
      }),
    ),
    value => new Offset(value),
);

export function character(str: string) {
  if (str === '') {
    return;
  }
  const c1 = str.charCodeAt(0);
  if (c1 >= 0xD800 && c1 < 0xDC00) {
    const c2 = str.charCodeAt(1);
    if (c2 < 0xDC00 || c2 >= 0xE000) {
    }
    return { value: str.slice(0, 2), next: str.slice(2) };
  }
  return { value: str[0], next: str.slice(1) };
}

export const idAssertion = conv(
  ['[', stringEscapedSpecialChars, ']'],
  (([, id]:[any, string]) => id),
);

export const step = conv(
  ['/', integer, opt(idAssertion)],
  (([, value, id]:[any, number, string | undefined]) => new Child(value, id)),
);

export const localPath = conv(rep(step), steps => new LocalPath(...steps));

export const localPaths = rep(localPath, '!');

export const path = conv(
  [localPaths,
    opt(offset),
    opt(conv(['[', assertion, ']'], ([, assertion]:['[', Assertion, ']']) => assertion)),
  ],
  ([paths, offs, assertion]:[LocalPaths, Offset | undefined, Assertion | undefined]) => {
    return new Path(paths, offs, assertion);
  },
);

export const range = conv(
  [localPaths, ',', path, ',', path],
  ([parent, , start, , end]:[LocalPaths, ',', Path, ',', Path]) => {
    return new CFIRange(parent, start, end);
  },
);

export const cfi = or(range, path);

export const cfiFragment = conv(
  ['epubcfi(', cfi, ')'],
  ([, cfi]:[any, CFIRange | Path]) => cfi,
);
